/**
 * RavenChat API Layer
 *
 * Centralizes all REST/fetch calls for the AgentHawk chat, session history,
 * and memory APIs. Keeps component files focused on UI logic.
 */

import { ApolloClient } from '@apollo/client';
import { QUERY_REGISTRY } from './autoSuggestions/queryRegistry';
import {
  API_BASE,
  AUTH_RELOAD_KEY,
  DEFAULT_PAGE_SIZE,
  DEFAULT_EVENTS_PAGE_SIZE,
  INSIGHTS_LOOKBACK_MS,
} from './constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export enum InsightPriority {
  CRITICAL = 'CRITICAL',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}
export enum InsightStatus {
  ACTIVE = 'ACTIVE',
  RESOLVED = 'RESOLVED',
  DISMISSED = 'DISMISSED',
}
export enum InsightType {
  RECOMMENDATION = 'RECOMMENDATION',
  EVENT = 'EVENT',
}

export interface ContextInsight {
  id: string;
  name: string;
  displayName: string;
  priority: InsightPriority;
  status: InsightStatus;
  type: InsightType;
  creationTime: number;
  updateTime: number;
}

export interface ContextClusterHealth {
  clusterHealth: any;
  nodes: { numberOfNodes: number };
  clusterStats: any;
  workloadClassification: any;
}

export interface ContextClusterInfo {
  domain: {
    es60: {
      cluster: { health: any; stats: any; settings: any };
    };
  };
}

export interface ContextDomainOverview {
  domain: {
    esVersion: string;
    cellId: string;
    carbonDomain: boolean;
    IsSensitiveDomain: boolean;
    DomainCreateDateTime: string;
    [key: string]: any;
  };
}

export interface ContextTmsIssue {
  issueType: string;
  tmsUpdateTimestamp: string;
  tmsCreateTimestamp: string;
  details: string;
  supportSOP: string;
  serviceSOP: string;
}

export interface ContextKeyEvent {
  event_time: string;
  source: string;
  action: string;
  action_state: string;
  host: string;
  domain_ident: string;
  id: string;
  tagging: string;
  version: string;
  dynamicFields: any;
}

export interface DomainContext {
  insights: { record: { insights: ContextInsight[] } } | null;
  clusterHealth: ContextClusterHealth | null;
  clusterInfo: ContextClusterInfo | null;
  domainOverview: ContextDomainOverview | null;
  tmsIssues: ContextTmsIssue[] | null;
  diagnosticDumpsExist: boolean | null;
  keyEvents: ContextKeyEvent[] | null;
  fetchedAt: number;
}

export interface SessionSummary {
  sessionId: string;
  createdAt: string | number;
  updatedAt?: string | number;
  actorId: string;
}

export interface SessionEvent {
  eventId: string;
  eventTimestamp: number;
  payload: Array<EventPayloadItem>;
}

export interface EventPayloadItem {
  conversational?: {
    content: { text: string };
    role: 'USER' | 'ASSISTANT' | 'TOOL' | 'OTHER';
  };
  blob?: string | Record<string, any>;
}

export interface InsightSummary {
  displayName: string;
  priority: string;
  status: string;
  type: string;
  description?: string;
  recommendations?: string;
  details?: string;
  detailError?: string;
}

// ─── Auth Helper ─────────────────────────────────────────────────────────────

function parseCookies(cookieString: string): Record<string, string> {
  return cookieString.split(';').reduce((acc, c) => {
    const [key, ...rest] = c.trim().split('=');
    if (key) acc[key] = rest.join('=');
    return acc;
  }, {} as Record<string, string>);
}

/**
 * Extract the Cognito username (actorId) from the JWT stored in cookies.
 * Returns null if the token is missing or unparseable.
 */
export function extractActorId(): string | null {
  const cookies = parseCookies(document.cookie);

  const lastAuthKey = Object.keys(cookies).find(
    (k) =>
      k.startsWith('CognitoIdentityServiceProvider.') &&
      k.endsWith('.LastAuthUser')
  );
  if (!lastAuthKey) return null;

  const prefix = lastAuthKey.replace('.LastAuthUser', '');
  const lastAuthUser = cookies[lastAuthKey];
  if (!lastAuthUser) return null;

  const idToken = cookies[`${prefix}.${lastAuthUser}.idToken`];
  if (!idToken) return null;

  try {
    const base64Url = idToken.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return (
      payload['cognito:username'] ||
      payload['username'] ||
      payload['sub'] ||
      null
    );
  } catch {
    return null;
  }
}

// ─── Chat API ────────────────────────────────────────────────────────────────

export async function sendChatMessage(
  message: string,
  sessionId: string | null,
  domainIdentifier: string,
  skipMemory?: boolean,
  skipFormat?: boolean,
  cellId?: string,
  context?: DomainContext
): Promise<{ message: string; sessionId?: string }> {
  const res = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      message,
      sessionId,
      domainIdentifier,
      ...(skipMemory && { skipMemory }),
      ...(skipFormat && { skipFormat }),
      ...(cellId && { cellId }),
      ...(context && { context }),
    }),
  });
  if (!res.ok) {
    // Cognito token expired — reload triggers the auth redirect flow
    // which obtains a fresh JWT without requiring manual user action.
    // Guard prevents infinite reload loop if re-auth itself fails.
    if (res.status === 401) {
      const key = AUTH_RELOAD_KEY;
      const lastReload = sessionStorage.getItem(key);
      if (!lastReload || Date.now() - Number(lastReload) > 10_000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();
      }
      throw new Error(
        'Session expired. Please refresh the page to re-authenticate.'
      );
    }
    let errMsg = `Chat API returned ${res.status}: ${res.statusText}`;
    try {
      const body = await res.json();
      if (body.message) errMsg = body.message;
    } catch {}
    throw new Error(errMsg);
  }
  return res.json();
}

// ─── Streaming Chat API ──────────────────────────────────────────────────────

/**
 * Wire-protocol events produced by Merlin's streaming endpoint. The BFF pipes
 * these through `/api/agentHawk/chat-stream` as Server-Sent Events without any
 * transformation. Clients should tolerate unknown event types (forward-compat).
 */
export type MerlinStreamEvent =
  | { type: 'token'; delta: string }
  | {
      type: 'tool_start';
      id: string;
      agent: string;
      input: Record<string, unknown>;
    }
  | {
      type: 'tool_end';
      id: string;
      agent: string;
      output: Record<string, unknown>;
    }
  | {
      type: 'subagent_tool_start';
      parent_agent: string;
      id: string;
      tool: string;
      input: unknown;
    }
  | {
      type: 'subagent_tool_end';
      parent_agent: string;
      id: string;
      tool: string;
      output: unknown;
    }
  | {
      type: 'subagent_reasoning';
      parent_agent: string;
      text: string;
    }
  | {
      type: 'subagent_state';
      agent: string;
      state: string;
      task: string;
    }
  | { type: 'subagent_token'; agent: string; delta: string }
  | { type: 'final'; content: string | Record<string, unknown> }
  | { type: 'error'; error_type: string; message: string; request_id?: string }
  | { type: 'heartbeat' }
  // Allow unknown future event types without crashing consumers.
  | { type: string; [key: string]: unknown };

/**
 * Stream a chat response from the AgentHawk streaming endpoint.
 *
 * Opens a POST request to `/api/agentHawk/chat-stream` and yields each SSE
 * event as it arrives. Yields events in the order the server emits them:
 * interleaved token/tool_start/tool_end/heartbeat, followed by exactly one
 * terminal `final` or `error` event.
 *
 * Callers can abort a stream mid-flight by passing an `AbortSignal`; the
 * BFF tears the upstream AgentCore connection down when the browser
 * disconnects.
 *
 * Implementation note: we use `fetch().body.getReader()` rather than
 * EventSource because EventSource doesn't support POST or `credentials:
 * 'include'` for cross-origin cookies.
 */
export async function* sendChatMessageStream(
  message: string,
  sessionId: string | null,
  domainIdentifier: string,
  options: {
    signal?: AbortSignal;
    onSessionId?: (sessionId: string) => void;
    cellId?: string;
    stack?: string;
    context?: DomainContext;
  } = {}
): AsyncGenerator<MerlinStreamEvent, void, void> {
  const url = `${API_BASE}/chat-stream`;
  const requestBody = {
    message,
    sessionId,
    domainIdentifier,
    ...(options.cellId && { cellId: options.cellId }),
    ...(options.stack && { stack: options.stack }),
    ...(options.context && { context: options.context }),
  };

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    credentials: 'include',
    body: JSON.stringify(requestBody),
    signal: options.signal,
  });

  if (!res.ok) {
    // Cognito token expired — reload triggers the auth redirect flow
    // which obtains a fresh JWT without requiring manual user action.
    // Guard prevents infinite reload loop if re-auth itself fails.
    if (res.status === 401) {
      const key = AUTH_RELOAD_KEY;
      const lastReload = sessionStorage.getItem(key);
      if (!lastReload || Date.now() - Number(lastReload) > 10_000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();
      }
      throw new Error(
        'Session expired. Please refresh the page to re-authenticate.'
      );
    }
    throw new Error(
      `Chat stream API returned ${res.status}: ${res.statusText}`
    );
  }
  if (!res.body) {
    throw new Error('Chat stream API returned no response body');
  }

  // Server echoes the chosen/generated session id on the response headers so
  // callers can learn it before any SSE frames arrive.
  const returnedSessionId = res.headers.get('X-Session-Id');
  if (returnedSessionId && options.onSessionId) {
    try {
      options.onSessionId(returnedSessionId);
    } catch {
      /* never let a bad callback tear down the stream */
    }
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by a blank line (\n\n). Each event can span
      // multiple `data: ` lines; we concatenate them before JSON-parsing.
      let sepIdx: number;
      // eslint-disable-next-line no-cond-assign
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);

        const dataLines: string[] = [];
        for (const line of rawEvent.split('\n')) {
          if (line.startsWith('data: ')) {
            dataLines.push(line.slice(6));
          } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5));
          }
          // Ignore comments (': keepalive'), `event:` lines, etc.
        }
        if (dataLines.length === 0) continue;

        const payload = dataLines.join('\n');
        let evt: MerlinStreamEvent;
        try {
          evt = JSON.parse(payload) as MerlinStreamEvent;
        } catch {
          // Never kill the stream over a single bad frame.
          continue;
        }
        yield evt;
      }
    }
  } finally {
    // Cancel the reader (releases the lock AND signals the underlying fetch
    // to tear down its TCP connection). Using releaseLock() alone would
    // leave the server streaming into a reader nobody is draining, wasting
    // the full agent budget's worth of bytes.
    try {
      await reader.cancel();
    } catch {
      /* ignore */
    }
  }
}

/**
 * The AgentCore coordinate of one written chat turn.
 *
 * Structurally identical to `MerlinFeedback/types.ts`'s `MemoryRef` and deliberately declared here rather
 * than imported: this module is the chat transport and must not depend on the feedback feature. The
 * consumer widens it back to the shared type.
 */
export interface WrittenMemoryRef {
  sessionId: string;
  eventId: string;
  role: 'ASSISTANT';
}

/** Bounds on the retry. Small: this runs while the user is looking at a finished answer. */
const CREATE_EVENT_MAX_ATTEMPTS = 3;
const CREATE_EVENT_RETRY_BASE_MS = 300;

/** Transport failure, throttling and server-side errors are worth retrying. Nothing else is. */
function isTransient(status: number | null): boolean {
  if (status === null) return true; // fetch rejected outright — no response at all
  if (status === 429) return true;
  return status >= 500;
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Write one chat turn to AgentCore Memory and return its coordinate.
 *
 * Returns `null` once the bounded retries are exhausted, which the caller renders as a response whose
 * rating controls are visible but inoperable — never as a failed answer. The answer itself is unaffected
 * by anything here.
 *
 * **One idempotency token per response, reused on every attempt.** Without a stable token a retry after a
 * request that timed out but had in fact succeeded writes a *second* event for the same turn: two
 * `eventId` values for one response, two target references, and the same answer rateable twice under two
 * different keys. Nothing in the store would look wrong. The token is minted here rather than in the
 * route for exactly that reason — see the matching comment in `raven.ts`.
 *
 * **This is not redundant with the Merlin Runtime's own chat write.** The Runtime codes one but spawns it
 * detached (`turn_stream` → `_spawn_commit` from a `finally`), nothing awaits it, and every failure is
 * swallowed. Empirically this call is the only conversational write that lands: all 12 sessions in the
 * gamma memory are ours. And the Runtime's `eventId` never reaches the browser anyway — the SSE terminal
 * `final` frame carries content only — so it is no substitute for the handle returned here.
 */
export async function writeEventToMemory(
  sessionId: string,
  userText: string,
  assistantText: string,
  domainIdentifier?: string
): Promise<WrittenMemoryRef | null> {
  const clientToken = makeClientToken();
  const body = JSON.stringify({
    payload: [
      { conversational: { content: { text: userText }, role: 'USER' } },
      {
        conversational: { content: { text: assistantText }, role: 'ASSISTANT' },
      },
    ],
    clientToken,
    ...(domainIdentifier && { domainIdentifier }),
  });

  for (let attempt = 1; attempt <= CREATE_EVENT_MAX_ATTEMPTS; attempt++) {
    let status: number | null = null;
    /**
     * A 2xx whose body would not parse — tracked separately from `status`, which here says the opposite.
     *
     * `res.json()` rejecting lands in the outer catch with `status` already set to 200, and
     * `isTransient(200)` is false, so the retry decision below would give up on a write the server had just
     * acknowledged. That is the worst available reading of the evidence: the event most likely exists and
     * only its identifier was lost, yet returning `null` marks the response permanently unrateable.
     *
     * Retrying is not a guess that the next body will parse. `clientToken` is minted once, above this loop,
     * so every attempt carries the same one and the server deduplicates — if the write did land, the retry
     * returns that same event's id rather than creating a second one. The id is recoverable, and it exists
     * nowhere else: only in this response body.
     */
    let unparseableOk = false;
    try {
      const res = await fetch(
        `${API_BASE}/sessions/${encodeURIComponent(sessionId)}/create-event`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body,
        }
      );
      status = res.status;
      if (res.ok) {
        let created: any;
        try {
          created = await res.json();
        } catch {
          unparseableOk = true;
        }
        if (!unparseableOk) {
          const eventId = created && created.eventId;
          // A 2xx carrying no eventId is a failure, not a success: there is no handle to rate against.
          // Distinct from an unparseable body — this one was read and genuinely holds no id, so there is
          // nothing a retry could recover.
          if (typeof eventId === 'string' && eventId.length > 0) {
            return { sessionId, eventId, role: 'ASSISTANT' };
          }
          return null;
        }
      }
    } catch (err: any) {
      console.warn(
        '[AgentHawk] create-event attempt failed:',
        err && err.message
      );
    }

    /**
     * A lapsed session, reported rather than folded into the generic give-up.
     *
     * This path deliberately does **not** trigger the reload/auth-redirect the rest of this module uses on a
     * 401, and the difference is worth stating because it looks like an oversight. Those calls are the user's
     * own action — sending a message, opening history — so reloading costs them nothing. This one runs *after*
     * the assistant response has rendered, so reloading would discard the answer in order to restore the
     * ability to rate it, which inverts the value of the two. The user is not stranded either: if the token
     * has lapsed, their next message goes through `sendChatMessage`, which does drive re-auth.
     *
     * What was wrong before is that a 401 was indistinguishable from retry exhaustion — both simply returned
     * `null` and the controls vanished. Logged distinctly so "rating disappeared" can be told apart from
     * "AgentCore refused three times".
     */
    if (status === 401) {
      console.warn(
        '[AgentHawk] create-event unauthorised; the response cannot be rated. ' +
          'Not reloading here — that would discard the answer. Re-auth happens on the next message.'
      );
      return null;
    }

    // A malformed rejection will fail identically every time, so retrying only delays telling the user that
    // rating is unavailable. An unparseable 2xx is the exception: the status says "not transient" while the
    // situation is unknown, so it is retried on its own account.
    if (!unparseableOk && !isTransient(status)) return null;
    if (attempt < CREATE_EVENT_MAX_ATTEMPTS) {
      await delay(CREATE_EVENT_RETRY_BASE_MS * Math.pow(2, attempt - 1));
    }
  }
  return null;
}

function makeClientToken(): string {
  // Charset kept to what the route's SAFE_CLIENT_TOKEN accepts.
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ─── Session History API ─────────────────────────────────────────────────────

export async function fetchSessions(
  actorId: string,
  domainIdentifier?: string,
  nextToken?: string
): Promise<{ sessions: SessionSummary[]; nextToken?: string }> {
  const res = await fetch(`${API_BASE}/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      actorId,
      maxItems: DEFAULT_PAGE_SIZE,
      ...(nextToken && { nextToken }),
      ...(domainIdentifier && { domainIdentifier }),
    }),
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch sessions (${res.status})`);
  }

  const data = await res.json();
  return {
    sessions: data.sessionSummaries || data.sessions || [],
    nextToken: data.nextToken ?? undefined,
  };
}

export async function fetchSessionEvents(
  sessionId: string,
  actorId: string,
  domainIdentifier?: string,
  nextToken?: string
): Promise<{ events: SessionEvent[]; nextToken?: string }> {
  if (nextToken !== undefined && !nextToken) {
    return { events: [], nextToken: undefined };
  }

  const res = await fetch(
    `${API_BASE}/sessions/${encodeURIComponent(sessionId)}/events`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        actorId,
        maxItems: DEFAULT_EVENTS_PAGE_SIZE,
        ...(nextToken && { nextToken }),
        ...(domainIdentifier && { domainIdentifier }),
      }),
    }
  );

  if (!res.ok) {
    throw new Error(`Failed to fetch events (${res.status})`);
  }

  const data = await res.json();
  return {
    events: data.events || [],
    nextToken: data.nextToken ?? undefined,
  };
}

// ─── Ticket Analysis Sessions API ─────────────────────────────────────────────

export interface TicketSession {
  sessionId: string;
  ticketId: string;
  createdAt: string | number;
  updatedAt?: string | number;
  actorId: string;
}

export interface MemorySessionContent {
  events: SessionEvent[];
  nextToken?: string;
  /** Present when the session only holds the BFF's processing marker: the epoch ms the analysis started. */
  processingStartedAt?: number;
}

/**
 * List all ticket analysis sessions for a domain.
 * Sessions are stored under the 'ticket_interceptor' actor with
 * sessionId format: {accountId}_{domainName}_{ticketId}.
 * The server fetches all pages internally and returns the complete filtered list.
 */
export async function fetchTicketSessions(domainIdentifier: string): Promise<{
  sessions: TicketSession[];
  sessionPrefix: string;
  totalCount: number;
}> {
  console.log('[AgentHawk API] fetchTicketSessions called with:', {
    domainIdentifier,
  });
  const res = await fetch(`${API_BASE}/ticket-sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ domainIdentifier }),
  });

  if (!res.ok) {
    if (res.status === 401) {
      const key = AUTH_RELOAD_KEY;
      const lastReload = sessionStorage.getItem(key);
      if (!lastReload || Date.now() - Number(lastReload) > 10_000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();
      }
      throw new Error(
        'Session expired. Please refresh the page to re-authenticate.'
      );
    }
    const errBody = await res.text().catch(() => '');
    console.error(
      '[AgentHawk API] fetchTicketSessions error:',
      res.status,
      errBody
    );
    throw new Error(
      `Failed to fetch ticket sessions (${res.status}): ${errBody}`
    );
  }

  const data = await res.json();
  console.log('[AgentHawk API] fetchTicketSessions response:', data);
  return {
    sessions: data.sessions || [],
    sessionPrefix: data.sessionPrefix || '',
    totalCount: data.totalCount || 0,
  };
}

/**
 * Fetch memory content (events) for a ticket analysis session.
 */
export async function fetchTicketSessionContent(
  sessionId: string,
  nextToken?: string
): Promise<MemorySessionContent> {
  const res = await fetch(
    `${API_BASE}/ticket-sessions/${encodeURIComponent(sessionId)}/content`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...(nextToken && { nextToken }),
      }),
    }
  );

  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(
      `Failed to fetch ticket session content (${res.status}): ${errBody}`
    );
  }

  const data = await res.json();
  return {
    events: data.events || [],
    nextToken: data.nextToken ?? undefined,
    processingStartedAt: typeof data.processingStartedAt === 'number' ? data.processingStartedAt : undefined,
  };
}

/**
 * Trigger a non-streaming ticket analysis. The BFF writes the processing marker to the ticket's Memory
 * session (which moves the ticket from Active to Analyzed for every viewer) and invokes AgentCore. The
 * Runtime persists the analysis into the same session, so a `loadSessions()` after this returns will
 * surface the analyzed content in the Analyzed row.
 */
export async function triggerTicketAnalysis(
  domainIdentifier: string,
  ticketId: string,
  description?: string
): Promise<{ sessionId: string; ticketId: string; analysis: string }> {
  const res = await fetch(`${API_BASE}/trigger-ticket-analysis`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      domainIdentifier,
      ticketId,
      ...(description ? { description } : {}),
    }),
  });
  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`Failed to trigger ticket analysis (${res.status}): ${errBody}`);
  }
  return res.json();
}

/**
 * Import a ticket analysis session into the current user's memory.
 * Creates a new session with the ticket conversation copied over.
 */
export async function importTicketSession(
  sessionId: string,
  domainIdentifier: string
): Promise<{ newSessionId: string; copiedEvents: number }> {
  console.log('[AgentHawk API] importTicketSession called with:', {
    sessionId,
    domainIdentifier,
  });
  const res = await fetch(`${API_BASE}/import-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sessionId, domainIdentifier }),
  });

  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`Failed to import session (${res.status}): ${errBody}`);
  }

  const data = await res.json();
  console.log('[AgentHawk API] importTicketSession response:', data);
  return {
    newSessionId: data.newSessionId,
    copiedEvents: data.copiedEvents || 0,
  };
}

// ─── Insights API ────────────────────────────────────────────────────────────

/** Cached insights per domain identifier */
const insightsCache: Record<string, InsightSummary[]> = {};

/**
 * Fetch insights and their details for a domain.
 * Results are cached per domainIdentifier — subsequent calls return cached data.
 */
export async function fetchInsightsWithDetails(
  apolloClient: ApolloClient<object>,
  domainIdentifier: string
): Promise<{ insights: InsightSummary[]; hasActive: boolean }> {
  // Return cached if available
  if (insightsCache[domainIdentifier]) {
    const cached = insightsCache[domainIdentifier];
    return {
      insights: cached,
      hasActive: cached.some((i) => i.status === 'ACTIVE'),
    };
  }

  const insightsConfig = QUERY_REGISTRY['GET_INSIGHTS'];
  const now = Date.now();
  const vars = {
    ...insightsConfig.buildVariables(domainIdentifier),
    startTime: now - INSIGHTS_LOOKBACK_MS,
    endTime: now,
  };

  const { data } = await apolloClient.query({
    query: insightsConfig.gqlQuery!,
    variables: vars,
    fetchPolicy: 'network-only',
  });

  const insights = data?.getInsights?.record?.insights;
  if (!insights?.length) {
    insightsCache[domainIdentifier] = [];
    return { insights: [], hasActive: false };
  }

  const activeInsights = insights.filter((i: any) => i.status === 'ACTIVE');
  if (!activeInsights.length) {
    insightsCache[domainIdentifier] = [];
    return { insights: [], hasActive: false };
  }

  // Fetch details for each active insight
  const detailsConfig = QUERY_REGISTRY['GET_INSIGHT_DETAILS'];
  const summaries: InsightSummary[] = await Promise.all(
    activeInsights.map(async (insight: any) => {
      const summary: InsightSummary = {
        displayName: insight.displayName || insight.name,
        priority: insight.priority,
        status: insight.status,
        type: insight.type,
      };

      try {
        const detailVars = {
          ...detailsConfig.buildVariables(domainIdentifier),
          insightID: insight.id,
        };
        const { data: detailData } = await apolloClient.query({
          query: detailsConfig.gqlQuery!,
          variables: detailVars,
          fetchPolicy: 'network-only',
        });

        const fields = detailData?.getInsightDetails?.record?.fields;
        if (fields) {
          const desc = fields.find((f: any) => f.name === 'Description')?.value;
          if (desc) summary.description = desc;

          const recsField = fields.find(
            (f: any) => f.name === 'Recommendations'
          )?.value;
          if (recsField) {
            try {
              const recs = JSON.parse(recsField);
              summary.recommendations = recs
                .map((r: any) =>
                  r.recommendationOptions?.map((o: any) => o.title).join(', ')
                )
                .join('; ');
            } catch {
              summary.recommendations = 'Available';
            }
          }

          const detailsHtml = fields.find(
            (f: any) => f.name === 'Details'
          )?.value;
          if (detailsHtml) {
            summary.details = detailsHtml
              .replace(/<[^>]*>/g, ' ')
              .replace(/\s+/g, ' ')
              .trim()
              .slice(0, 500);
          }
        }
      } catch (err: any) {
        summary.detailError = err.message;
      }

      return summary;
    })
  );

  insightsCache[domainIdentifier] = summaries;
  return { insights: summaries, hasActive: true };
}

/** Clear cached insights for a domain (e.g. on retry) */
export function clearInsightsCache(domainIdentifier: string): void {
  delete insightsCache[domainIdentifier];
}
